import React, { Suspense } from "react";
import { useTenant } from "../contexts/TenantContext";
import { TenantConfig } from "../types/tenant";

// Lazy load components
const TermsHeader = React.lazy(() => import("../components/layout/terms/TermsHeader"));
const ChangesSection = React.lazy(() => import("../components/layout/terms/ChangesSection"));
const SiteContentSection = React.lazy(
  () => import("../components/layout/terms/SiteContentSection")
);
const AccountSecuritySection = React.lazy(
  () => import("../components/layout/terms/AccountSecuritySection")
);
const ThirdPartySection = React.lazy(() => import("../components/layout/terms/ThirdPartySection"));
const LicenseSection = React.lazy(() => import("../components/layout/terms/LicenseSection"));
const ObligationsSection = React.lazy(
  () => import("../components/layout/terms/ObligationsSection")
);
const IntellectualPropertySection = React.lazy(
  () => import("../components/layout/terms/IntellectualPropertySection")
);
const DisclaimerSection = React.lazy(() => import("../components/layout/terms/DisclaimerSection"));
const LiabilitySection = React.lazy(() => import("../components/layout/terms/LiabilitySection"));
const IndemnificationSection = React.lazy(
  () => import("../components/layout/terms/IndemnificationSection")
);
const ArbitrationSection = React.lazy(
  () => import("../components/layout/terms/ArbitrationSection")
);
const TCPASection = React.lazy(() => import("../components/layout/terms/TCPASection"));
const ContactSection = React.lazy(() => import("../components/layout/terms/ContactSection"));

const LoadingFallback = () => (
  <div className="animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
    <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
  </div>
);

const TermsPage: React.FC = () => {
  const { config } = useTenant() as { config: TenantConfig };
  const orgName = config?.orgData?.org_name || "";
  const orgEmail = config?.orgData?.email || "";
  const orgPhone = config?.orgData?.org_phone || "";
  const orgAddress = config?.orgData?.client_address;
  const formattedAddress = orgAddress
    ? `${orgAddress.street}, ${orgAddress.city}, ${orgAddress.state} ${orgAddress.zip}`
    : "";
  const domain =
    (typeof window !== "undefined" && window.location.hostname) ||
    config?.domain?.primary ||
    "speculo.ai";
  const primaryColor = config?.branding?.colors.primary;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col max-w-4xl mx-auto gap-2 lg:gap-5">
        <Suspense fallback={<LoadingFallback />}>
          <TermsHeader
            domain={domain}
            orgName={orgName}
            primaryColor={primaryColor}
          />

          <ChangesSection />
          <SiteContentSection />
          <AccountSecuritySection />
          <ThirdPartySection />
          <LicenseSection />
          <ObligationsSection />
          <IntellectualPropertySection
            orgEmail={orgEmail}
            formattedAddress={formattedAddress}
            primaryColor={primaryColor}
          />
          <DisclaimerSection />
          <LiabilitySection />
          <IndemnificationSection />
          <ArbitrationSection />
          <TCPASection
            primaryColor={primaryColor}
            orgEmail={orgEmail}
            formattedAddress={formattedAddress}
          />
          <ContactSection
            orgName={orgName}
            orgEmail={orgEmail}
            formattedAddress={formattedAddress}
            primaryColor={primaryColor}
          />
        </Suspense>
      </div>
    </div>
  );
};

export default TermsPage;
